import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAxios } from "../../common_nextgen/axios/AxiosHelper";
import FunctionKeys from "../../common_nextgen/common_components/FunctionKeys/FunctionKeys";
import useCustomNavigate from "../../common_nextgen/common_components/Navigation/NextGenNavigation";
import Form from "../../common_nextgen/common_components/form/Form";
import { useToast } from "../../common_nextgen/hooks/ToastContext";
import {
  PRICE_SCHEDULE_MODES,
  ROUTE_PATHS,
} from "../../common_nextgen/utils/Constants";
import {
  isAddMode,
  isEditMode,
  isReadOnlyMode,
} from "../../common_nextgen/utils/UtilFunctions";
import { formFields } from "./ProcessorFeeFormConfig";

const screenInfo = {
  ADD: ["Add Processor Fee Table", "RCPF3002"],
  CHANGE: ["Processor Fee Table", "RCPF3003"],
  VIEW: ["Processor Fee Table", "RCPF3003"],
};

const ProcessorFeeForm = ({ mode }) => {
  const location = useLocation();
  const { axios_helper_no_header } = useAxios();
  const [stateVal, setStateVal] = useState(location.state || []);
  const { currentState, previousState } = useMemo(() => {
    let [currentState = {}] = [...stateVal].reverse();
    const previousState = [...stateVal];
    previousState.splice(stateVal.length - 1, 1);
    return {
      previousState,
      currentState,
    };
  }, [stateVal]);

  const {
    selectedSchedules = [],
    previousScreen = ROUTE_PATHS.PROCESSOR_FEE_TABLE,
    currentIndex: initialIndex = 0,
    externalMode: initialExternalMode = false,
  } = currentState;
  const navigateToPage = useCustomNavigate();
   const [externalMode, setExternalMode] = useState(initialExternalMode);
  const [selectedOption, setSelectedOption] = useState("no");
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [currentMode, setCurrentMode] = useState(mode);
  const [initialFormData, setInitialFormData] = useState({});
  const [dataForSubmission, setDataForSubmission] = useState({});
  const [constraintErrors, setConstraintErrors] = useState([]);
  const toast = useToast();

  const handleBack = () => {
     if (externalMode) {
      const message = {
        message: "navigate-to-external-program",
        url: previousScreen,
        parameters: {
          state: [...previousState],
        },
      };
      console.log("back messages", message);
      window.parent.postMessage(message, "*");
    } else if (currentIndex < selectedSchedules.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentMode(mode);
    } else {
      navigateToPage(previousScreen, previousState);
    }
  };

  const getPageTitle = () => {
    const [screenTitle, screenNumber] = isAddMode(mode)
      ? screenInfo.ADD
      : screenInfo.CHANGE;
    return (
      <>
        <span className="title">{screenTitle}</span>
        <span>{"-"}</span>
        <span className="screen-number">{screenNumber}</span>
      </>
    );
  };

  const submitFormData = (data) => {
    setDataForSubmission(data);
  };

  const submitForm = async (dataForSubmission) => {
    let url = "";
    let verb = "";
    let screenNumber = "";

    if (isAddMode(currentMode)) {
      url = "api/pf3ProcessorFeTable";
      verb = "POST";
      screenNumber = screenInfo.ADD[1];
    } else {
      url = "api/pf3ProcessorFeTable";
      verb = "PUT";
      screenNumber = screenInfo.CHANGE[1];

      if (
        JSON.stringify(dataForSubmission) === JSON.stringify(initialFormData)
      ) {
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "No Change of Information",
          life: 3000,
        });
        return;
      }
    }

    const data = { ...dataForSubmission, screenNumber };

    try {
      const { responseStatus, responseData } = await axios_helper_no_header(
        verb,
        url,
        data,
        null
      );
      if (isAddMode(currentMode)) {
        setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
        navigateToPage(ROUTE_PATHS.PROCESSOR_FEE_TABLE_EDIT, [
          {
            paramsData: {
              pf3ProcFeeTable: responseData["pf3ProcFeeTable"],
            },
          },
          {
            selectedSchedules: [{ ...responseData }],
            currentIndex: 0,
            previousScreen: previousScreen,
          },
        ]);
        setStateVal([
          {
            paramsData: {
              pf3ProcFeeTable: responseData["pf3ProcFeeTable"],
            },
          },
          {
            selectedSchedules: [{ ...responseData }],
            currentIndex: 0,
            previousScreen: previousScreen,
          },
        ]);
      }
      setInitialFormData(responseData);
      toast.current.show({
        severity: "success",
        summary: "",
        detail: responseStatus.message,
        life: 3000,
      });
    } catch (error) {
      const { responseStatus: { displayErrorFields = null, message } = {} } =
        error.data;
      if (displayErrorFields === null) {
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      } else {
        setConstraintErrors(displayErrorFields);
      }
    }
  };

  const [keyFunc, keyName] = useMemo(() => {
    const keyFunc = {};
    const keyName = {};

    if (isAddMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
    } else if (isEditMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.F14 = () => setCurrentMode(PRICE_SCHEDULE_MODES.READ_ONLY);
      keyName.F14 = "View";
    } else if (isReadOnlyMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.F14 = () => setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
      keyName.F14 = "Edit";
    }

    return [keyFunc, keyName];
  }, [currentMode, currentIndex, externalMode, initialFormData]);

  useEffect(() => {
    const fetchInitialData = async () => {
      const url = isAddMode(currentMode)
        ? `api/pf3ProcessorFeTable/initialFormData/${screenInfo.ADD[1]}`
        : `api/pf3ProcessorFeTable/${encodeURIComponent(
            selectedSchedules[currentIndex].pf3ProcFeeTable
          )}`;
      const { responseData } = await axios_helper_no_header("GET", url);
      setInitialFormData(responseData);
    };

    if (
      !isAddMode(currentMode) &&
      !selectedSchedules?.[currentIndex]?.pf3ProcFeeTable
    ) return;
    fetchInitialData();
  }, [mode, currentIndex, currentMode, selectedSchedules]);

   useEffect(() => {
    const handleMessage = (event) => {
      const { type, data } = event.data;
      console.log("iframe load", type, data);
      if (type === "set-params") {
        setStateVal(data);
        setExternalMode(true);
      }
      console.log("message", event.data);
    };
     window.addEventListener("message", handleMessage);
     return () => window.removeEventListener("message", handleMessage);
   });

  return (
    <div className="grid container">
      <div className="col col-12">
        <FunctionKeys keyFunc={keyFunc} keyName={keyName} />
      </div>
      <div className="col col-12">
        <h1>{getPageTitle()}</h1>
      </div>
      <div className="col col-12">
        <Form
          formData={initialFormData}
          formFields={formFields}
          mode={currentMode}
          submitFormData={submitForm}
          constraintErrors={constraintErrors}
        />
      </div>
    </div>
  );
};

export default ProcessorFeeForm;
